import React from 'react'
import { Element, Message } from 'react-bulma-components'
import { useClockingsQuery } from '../../queries/clockings'
import { addMinutes, endOfDay, startOfDay, subMinutes } from 'date-fns'
import { timeInputValueToDate } from '../../utils/date'
import ClockingsTimeline from './clockings-timeline'

interface Props {
  userId: string
  date: Date
  workPeriodMatchRange?: { start: number; end: number }
  /** Form start time (HH:mm) — when provided, query range adapts to WP being created */
  startTime?: string
  /** Form end time (HH:mm) — when provided, query range adapts to WP being created */
  endTime?: string
}

/**
 * Queries orphaned clockings for a user/date range and renders them
 * as a ClockingsTimeline inside an info Message box.
 *
 * Used in forms where a WP is being created or modified — previews
 * which clockings will be picked up.
 */
const OrphanedClockingsInfo: React.FC<Props> = ({
  userId,
  date,
  workPeriodMatchRange,
  startTime,
  endTime,
}) => {
  const wpStart = startTime ? timeInputValueToDate(date, startTime) : null
  const wpEnd = endTime ? timeInputValueToDate(date, endTime) : null

  const queryStart =
    wpStart && workPeriodMatchRange
      ? subMinutes(wpStart, workPeriodMatchRange.start)
      : startOfDay(date)
  const queryEnd =
    wpEnd && workPeriodMatchRange ? addMinutes(wpEnd, workPeriodMatchRange.end) : endOfDay(date)

  const clockingsQuery = useClockingsQuery({
    user: [userId],
    start: queryStart,
    end: queryEnd,
    type: 'null',
    mission: 'null',
  })

  const orphans = (clockingsQuery.data || [])
    .filter(c => !c.mission && !c.workPeriod)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

  if (clockingsQuery.isLoading) return null
  if (orphans.length === 0) return null

  return (
    <Message color="info" mb={3}>
      <Message.Body>
        <Element renderAs="strong" mb={2}>
          {orphans.length} pointage(s) orphelin(s) seront pris en compte
        </Element>
        <ClockingsTimeline clockings={orphans} />
      </Message.Body>
    </Message>
  )
}

export default OrphanedClockingsInfo
